import React from "react";
import { Cell, Pie, PieChart, ResponsiveContainer } from "recharts";
import { Box, Grid, Typography } from "@mui/material";
import { groupByIntakeMethod } from "./_common/Analytics";
import SectionCard from "@/app/admin/_common/SectionCard";

import { PieChartSkeleton } from "./HomeDashboardSkeletons";
interface ChannelSignupsProps {
  isLoading: boolean;
  guests: any;
  height?: number;
  hideSubtitle?: boolean;
  compact?: boolean;
}
export default function ChannelSignups(props: ChannelSignupsProps) {
  const { isLoading, guests, height = 220, hideSubtitle, compact } = props;
  const pieBoxSize = compact ? { width: 110, height: 90 } : { width: 160, height: 120 };
  const outerRadius = compact ? 42 : 60;
  const innerRadius = compact ? 34 : 50;
  const totalFontSize = compact ? "16px" : "22px";
  const totalLabelFontSize = compact ? "10px" : "12px";

  const data = groupByIntakeMethod(guests?.data, "intake_method").sort(
    (a: any, b: any) => {
      const orderOf = (name: string) => {
        const idx = LEGEND_ORDER.indexOf(name);
        return idx === -1 ? LEGEND_ORDER.length : idx;
      };
      return orderOf(a.name) - orderOf(b.name);
    }
  );
  const total = data.reduce((sum: number, entry: any) => sum + entry.value, 0);

  return (
    <SectionCard
      isLoading={isLoading}
      loadingContent={<PieChartSkeleton />}
      alignContent={"start"}
      justifyContent={"start"}
      label={
        <Grid container direction={"column"} rowGap={0.25}>
          <Typography sx={{ color: "var(--cuo-text)", fontSize: "15px", fontWeight: 600 }}>
            Signups by channel
          </Typography>
          {!hideSubtitle && (
            <Typography sx={{ color: "var(--cuo-text-muted)", fontSize: "13px" }}>
              {`Where this week's ${guests?.guest_count ?? 0} signups came from`}
            </Typography>
          )}
        </Grid>
      }
      sx={{ height: `${height}px` }}
    >
      {!isLoading && (
        <Grid
          container
          alignItems={"center"}
          columnGap={5}
          wrap="nowrap"
          sx={{ width: "100%", height: "100%", px: 3, mb: 2 }}
        >
          <Box sx={{ position: "relative", ...pieBoxSize, flex: "none" }}>
            <ResponsiveContainer height={"100%"} width={"100%"}>
              <PieChart>
                <Pie
                  dataKey="value"
                  data={data}
                  cx="50%"
                  cy="50%"
                  outerRadius={outerRadius}
                  innerRadius={innerRadius}
                  paddingAngle={2}
                  stroke="none"
                  label={false}
                >
                  {data.map((entry: any, index: number) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={colorForChannel(entry.name, index)}
                    />
                  ))}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                pointerEvents: "none",
              }}
            >
              <Typography sx={{ fontSize: totalFontSize, fontWeight: 700, color: "var(--cuo-text)", lineHeight: 1.2 }}>
                {total.toLocaleString()}
              </Typography>
              <Typography sx={{ fontSize: totalLabelFontSize, color: "var(--cuo-text-muted)" }}>
                total
              </Typography>
            </Box>
          </Box>

          <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 0.5, minWidth: 0 }}>
            {data.map((entry: any, index: number) => (
              <Box
                key={`legend-${index}`}
                sx={{ display: "flex", alignItems: "center" }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
                  <Box
                    sx={{
                      width: 10,
                      height: 10,
                      borderRadius: "2px",
                      backgroundColor: colorForChannel(entry.name, index),
                      flexShrink: 0,
                    }}
                  />
                  <Typography sx={{ fontSize: "12px", color: "var(--cuo-text)" }}>
                    {entry.name}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    flex: 1,
                    mx: 1,
                    borderBottom: "1px dotted var(--cuo-border-color)",
                    transform: "translateY(-2px)",
                  }}
                />
                <Typography sx={{ fontSize: "12px", fontWeight: 400, color: "var(--cuo-text)" }}>
                  {entry.value.toLocaleString()}
                </Typography>
              </Box>
            ))}
          </Box>
        </Grid>
      )}
    </SectionCard>
  );
}

const LEGEND_ORDER = ["Instagram", "Stripe", "Other", "Already been to a show"];

const CHANNEL_COLORS: { [key: string]: string } = {
  Instagram: "#504BD2",
  Stripe: "#44AADA",
  Other: "#FFA215",
  "Already been to a show": "#D14886",
};

const FALLBACK_COLORS = ["#51BC78", "#504BD2", "#F96B17", "#E4B415"];

const colorForChannel = (name: string, index: number) =>
  CHANNEL_COLORS[name] ?? FALLBACK_COLORS[index % FALLBACK_COLORS.length];
